import actionTypes from "store/Actions/types";
import * as backupModule from "./backup";

let syncTimer = null;
let inFlightCount = 0;
let lastLocalMutationAt = 0;
let suppressAutoSyncDepth = 0;

export const MUTATION_ACTION_TYPES = new Set([
    actionTypes.ADD_PORTFOLIO,
    actionTypes.DELETE_PORTFOLIO,
    actionTypes.ADD_ASSET,
    actionTypes.ADD_TRANSACTION,
    actionTypes.BUY,
    actionTypes.SELL,
    actionTypes.UPDATE_SETTINGS,
    actionTypes.UPDATE_THEME,
    actionTypes.UPDATE_BACKEDUP_ASSETS,
    actionTypes.ADD_WALLET,
    actionTypes.UPDATE_WALLET,
    actionTypes.DELETE_WALLET,
    actionTypes.UPDATE_BACKEDUP_WALLETS,
]);

export const runWithCloudHydrationGuard = (fn) => {
    suppressAutoSyncDepth += 1;
    try {
        return fn();
    } finally {
        suppressAutoSyncDepth = Math.max(0, suppressAutoSyncDepth - 1);
    }
};

export const getLastLocalMutationAt = () => lastLocalMutationAt;

export const isLocalSyncInFlight = () =>
    inFlightCount > 0 || syncTimer !== null;

export const resetAutoSyncState = () => {
    if (syncTimer) {
        clearTimeout(syncTimer);
        syncTimer = null;
    }
    inFlightCount = 0;
    lastLocalMutationAt = 0;
    suppressAutoSyncDepth = 0;
};

export const shouldIgnoreCloudSnapshot = (snapshot, data = {}) => {
    if (snapshot?.metadata?.hasPendingWrites) {
        return true;
    }
    if (isLocalSyncInFlight()) {
        return true;
    }
    const effectiveData =
        data && Object.keys(data).length > 0 ? data : snapshot || {};
    const remoteTime = Number(
        effectiveData?.updatedAt || effectiveData?.settings?.lastBackup || 0
    );
    if (
        lastLocalMutationAt > 0 &&
        remoteTime > 0 &&
        remoteTime < lastLocalMutationAt
    ) {
        return true;
    }
    return false;
};

export const buildSyncedSettingsPayload = (settings = {}, timestamp) => {
    const { syncStatus, ...persistedSettings } = settings || {};
    return {
        ...persistedSettings,
        lastBackup: timestamp,
    };
};

export const syncStoreToCloudNow = async (store) => {
    if (!store || typeof store.getState !== "function") {
        return false;
    }
    const state = store.getState() || {};
    const userInfo = state.users?.userInfo;
    if (!userInfo || !(userInfo.uid || userInfo.id || userInfo.email)) {
        return false;
    }

    const portfolios = state.portfolios?.portfolios || [];
    const assets = state.assets?.assets || [];
    const settings = state.settings || {};
    const wallets = state.wallets?.wallets || [];
    const now = Date.now();
    lastLocalMutationAt = Math.max(lastLocalMutationAt, now);

    inFlightCount += 1;
    store.dispatch({
        type: actionTypes.UPDATE_SYNC_STATUS,
        payload: "syncing",
    });

    try {
        const cleanSettings = buildSyncedSettingsPayload(settings, now);
        await backupModule.performBackup(
            "users",
            userInfo,
            portfolios,
            assets,
            cleanSettings,
            { silent: true, wallets }
        );
        store.dispatch({
            type: actionTypes.UPDATE_LAST_BACKUP,
            payload: now,
        });
        store.dispatch({
            type: actionTypes.UPDATE_SYNC_STATUS,
            payload: "synced",
        });
        return true;
    } catch (err) {
        store.dispatch({
            type: actionTypes.UPDATE_SYNC_STATUS,
            payload: "error",
        });
        return false;
    } finally {
        inFlightCount = Math.max(0, inFlightCount - 1);
    }
};

const isSyncTriggeringAction = (action) => {
    if (!action || !action.type) return false;
    if (suppressAutoSyncDepth > 0 || action.meta?.fromCloud) {
        return false;
    }
    if (!MUTATION_ACTION_TYPES.has(action.type)) {
        return false;
    }
    if (action.type === actionTypes.UPDATE_SETTINGS) {
        const payload = action.payload || {};
        const keys = Object.keys(payload).filter(
            (k) =>
                k !== "syncStatus" &&
                k !== "lastBackup" &&
                payload[k] !== undefined
        );
        return keys.length > 0;
    }
    return true;
};

export const createAutoSyncMiddleware =
    (options = {}) =>
    (store) =>
    (next) =>
    (action) => {
        const debounceMs =
            typeof options === "number"
                ? options
                : Number(options?.debounceMs ?? 400);
        const result = next(action);
        if (!isSyncTriggeringAction(action)) {
            return result;
        }

        const state = store.getState?.() || {};
        const userInfo = state.users?.userInfo;
        if (!userInfo || !(userInfo.uid || userInfo.id || userInfo.email)) {
            return result;
        }

        lastLocalMutationAt = Date.now();
        store.dispatch({
            type: actionTypes.UPDATE_SYNC_STATUS,
            payload: "syncing",
        });

        if (syncTimer) {
            clearTimeout(syncTimer);
        }

        if (debounceMs <= 0) {
            syncTimer = null;
            syncStoreToCloudNow(store);
        } else {
            syncTimer = setTimeout(() => {
                syncTimer = null;
                syncStoreToCloudNow(store);
            }, debounceMs);
        }

        return result;
    };

export default createAutoSyncMiddleware;
